import React from 'react';
import LegalPageLayout from './LegalPageLayout';
import classes from './LegalPage.module.scss';

const SlidingToonsPrivacy = () => {
  return (
    <LegalPageLayout
      title="Privacy Policy — Sledtoons"
      lastUpdated="5 October 2026"
      documentTitle="Sledtoons — Privacy Policy | Arsanyu Technologies"
      intro={
        <>
          This policy covers the Sledtoons mobile game (&ldquo;the game&rdquo;,
          package <strong>com.sledtoons.game</strong>), developed and published
          by <strong>Arsanyu Technologies Pvt. Ltd.</strong> (&ldquo;we&rdquo;,
          &ldquo;us&rdquo;).
        </>
      }
    >
      <section className={classes.callout}>
        <h2>The short version</h2>
        <p>
          Sledtoons does not ask you for your name, email address or any other
          directly identifying information, and it has no accounts or sign-in.
          Your game progress is saved on your own device.
        </p>
        <p>
          The game does show ads, measure how the game is used, and offer
          in-app purchases. To do those three things it shares device-level
          data — such as your device&rsquo;s advertising identifier, IP address
          and app usage events — with Google. The sections below set out
          exactly what is collected and by whom.
        </p>
      </section>

      <section>
        <h2>What the game stores on your device</h2>
        <p>
          The game keeps one save file in its private storage area on your
          device. It contains only your progress:
        </p>
        <ul>
          <li>Coins, gems, diamonds and tickets</li>
          <li>
            Upgrade levels, current level, best distance and best progress per
            level
          </li>
          <li>Cards collected, characters owned and which one is selected</li>
          <li>Daily task progress and chest timers</li>
          <li>Which in-app purchases you have unlocked</li>
          <li>
            A randomly generated identifier, created on first launch, used only
            to tell one save apart from another on the same device
          </li>
        </ul>
        <p>
          This save identifier is not derived from anything about you, your
          device or your accounts. It is a random number, and it is not sent to
          us or to anyone else.
        </p>
        <p>
          This file is private to the game. Uninstalling the game deletes it.
          You can also erase it at any time from inside the game:{' '}
          <strong>Settings → Reset Game</strong>. Resetting the game does not
          erase data already collected by the third parties named below — see{' '}
          <strong>Your choices</strong>.
        </p>
      </section>

      <section>
        <h2>Advertising</h2>
        <p>
          The game shows banner and interstitial ads, and optional rewarded
          video ads that you can choose to watch in exchange for in-game
          rewards. Ads are served by <strong>Google AdMob</strong>.
        </p>
        <p>To serve and measure ads, Google may collect and process:</p>
        <ul>
          <li>Your device&rsquo;s advertising identifier (Advertising ID)</li>
          <li>IP address and coarse device information (model, OS version)</li>
          <li>Ad interaction data (impressions, clicks, rewarded views)</li>
        </ul>
        <p>
          This processing is governed by Google&rsquo;s Privacy Policy:{' '}
          <a
            href="https://policies.google.com/privacy"
            target="_blank"
            rel="noopener noreferrer"
          >
            https://policies.google.com/privacy
          </a>
        </p>
        <p>
          For users in the EEA, the UK and Switzerland, a consent form is shown
          before any ad-related data is processed, as required by GDPR. If you
          decline, the game still works and ads are still shown, but they are
          non-personalized.
        </p>
      </section>

      <section>
        <h2>Analytics and crash reporting</h2>
        <p>
          The game uses <strong>Google Analytics for Firebase</strong> and{' '}
          <strong>Firebase Crashlytics</strong> to understand how the game is
          played and to find and fix crashes. Through these, Google collects
          and processes:
        </p>
        <ul>
          <li>
            Gameplay and app events — sessions, levels started and completed,
            progression, screens opened, purchases made
          </li>
          <li>
            Device and app information — device model, operating system
            version, language, country, app version
          </li>
          <li>
            A Firebase installation identifier and your device&rsquo;s
            advertising identifier
          </li>
          <li>
            Crash and error diagnostics — stack traces, and the device state at
            the time of a crash
          </li>
        </ul>
        <p>
          This data is used in aggregate to improve the game. It is processed
          by Google under Google&rsquo;s Privacy Policy:{' '}
          <a
            href="https://policies.google.com/privacy"
            target="_blank"
            rel="noopener noreferrer"
          >
            https://policies.google.com/privacy
          </a>
        </p>
      </section>

      <section>
        <h2>In-app purchases</h2>
        <p>
          The game offers optional in-app purchases. All purchases are handled
          by <strong>Google Play Billing</strong>. Google processes the
          payment; your payment method, card details and billing address go to
          Google and are never seen by, sent to, or stored by us.
        </p>
        <p>
          We receive only the confirmation that a particular purchase completed,
          so the game can unlock what you bought. That entitlement is recorded
          in the local save file described above.
        </p>
        <p>
          Purchase history and refunds are managed in your Google Play account.
          Google&rsquo;s handling of payment data is governed by Google&rsquo;s
          Privacy Policy:{' '}
          <a
            href="https://policies.google.com/privacy"
            target="_blank"
            rel="noopener noreferrer"
          >
            https://policies.google.com/privacy
          </a>
        </p>
      </section>

      <section>
        <h2>What the game does not do</h2>
        <ul>
          <li>
            It does not ask for or collect your name, email address, phone
            number or precise location.
          </li>
          <li>It does not use accounts or sign-in of any kind.</li>
          <li>It does not access your contacts, photos, camera or microphone.</li>
          <li>It has no chat and no social or messaging features.</li>
          <li>It does not sell your data.</li>
          <li>
            We do not operate servers of our own for this game and keep no
            player database. The data described above goes to Google as our
            processor, not to us.
          </li>
        </ul>
      </section>

      <section>
        <h2>Your choices</h2>
        <ul>
          <li>
            <strong>Ad personalization</strong> — in the EEA, UK and
            Switzerland you can change your consent at any time from{' '}
            <strong>Settings → Privacy options</strong> inside the game.
          </li>
          <li>
            <strong>Advertising ID</strong> — you can reset it, or opt out of
            ad personalization entirely, in your device settings under{' '}
            <strong>Settings → Privacy → Ads</strong>.
          </li>
          <li>
            <strong>Local data</strong> — <strong>Settings → Reset Game</strong>{' '}
            erases your save, and uninstalling the game removes it entirely.
          </li>
          <li>
            <strong>Requests about your data</strong> — write to us at the
            address below and we will act on it, including passing deletion
            requests to our processors where we are able to.
          </li>
        </ul>
      </section>

      <section>
        <h2>Permissions</h2>
        <p>The game requests the following Android permissions:</p>
        <ul>
          <li>
            <strong>Internet</strong> and <strong>network state</strong> — to
            load ads, report analytics and crashes, and complete purchases.
          </li>
          <li>
            <strong>Advertising ID</strong> — to serve and measure ads, as
            described above.
          </li>
          <li>
            <strong>Vibrate</strong> — used for controller feedback while
            riding. It grants no access to any information.
          </li>
        </ul>
      </section>

      <section>
        <h2>Links out of the game</h2>
        <p>
          The settings screen has a link to this policy, and ads may open pages
          outside the game. Opening either hands the address to your browser,
          which is then governed by your browser&rsquo;s own privacy policy and
          by the privacy policy of the site you land on, not ours.
        </p>
      </section>

      <section>
        <h2>Children</h2>
        <p>
          The game is not directed at children. It contains advertising and
          in-app purchases, and has no chat or social features. We do not
          knowingly collect data from children. If you believe a child has
          provided us with data, contact us at the address below and we will
          act on it.
        </p>
      </section>

      <section>
        <h2>Changes to this policy</h2>
        <p>
          This policy will be updated before any future version of the game
          changes what is collected or who it is shared with, and the
          &ldquo;last updated&rdquo; date above will change. Any changes are
          published at this same address.
        </p>
      </section>

      <section className={classes.contact}>
        <h2>Contact</h2>
        <p>
          Questions about this policy, or requests about your data:{' '}
          <a href="mailto:archit9spicin@gmail.com">archit9spicin@gmail.com</a>
        </p>
        <p>Arsanyu Technologies Pvt. Ltd.</p>
      </section>
    </LegalPageLayout>
  );
};

export default SlidingToonsPrivacy;
